import { z } from 'zod';
import { UserSummary } from './common';
import { TripItemStatus } from './enums';
import { longText, name, uuid } from './fields';
import { MasterList } from './master-lists';

/**
 * Trip Items — the working copy of an item on a Trip (data-model.md §10), the
 * central execution table. Carries everything execution needs plus a frozen
 * memory of its origin. REST endpoints land in T15/T16/T19/T20; DTO shapes
 * pinned now.
 *
 * Code-enforced invariants (data-model.md §10 — API logic, not schema):
 * working-copy isolation, frozen origin snapshot, `handledBy` defaults to
 * `createdBy` on manual add (NULL for pulled-in), `forPersonId`/`handledBy`
 * independent (no inference), question axis independent of status. These DTOs
 * make them expressible (nullable origin/handledBy/forPerson, separate status
 * vs question fields) but do NOT encode the behavior.
 */

const BaseTripItem = z.object({
  id: uuid(),
  tripId: uuid(),
  originMasterListId: uuid().nullable(),
  originMasterListName: z.string().nullable(),
  /** The specific library item this was pulled from (null = manual or unlinked). */
  originMasterListItemId: uuid().nullable(),
  /** Cross-list link: the controlling trip item whose status this mirrors (null = independent/controller). */
  controlledByItemId: uuid().nullable(),
  name: name(),
  description: longText(),
  status: TripItemStatus,
  /** Optional "final check" pass — confirmed physically packed. Implies done. */
  finalChecked: z.boolean(),
  flagged: z.boolean(),
  questionOpen: z.boolean(),
  questionText: z.string().nullable(),
  forPersonId: uuid().nullable(),
  handledBy: uuid().nullable(),
  handledByUser: UserSummary.nullable(),
  note: z.string().nullable(),
  position: z.number().int(),
  createdBy: uuid(),
  createdByUser: UserSummary.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deletedAt: z.string().datetime().nullable(),
  deletedBy: uuid().nullable(),
  /** Number of comments on this item (drives the checklist row's 💬 indicator). */
  commentCount: z.number().int().nonnegative(),
  /** Who wrote the most recent comment (null when there are none). Lets the row
   * read "someone else had the last word" without per-user read tracking. */
  lastCommentBy: uuid().nullable(),
});

/** Full Trip Item resource (response shape). */
export const TripItem = BaseTripItem.refine(
  (item) => item.originMasterListId === null || item.originMasterListName !== null,
  {
    message: 'originMasterListName is required when originMasterListId is set',
    path: ['originMasterListName'],
  },
);
export type TripItem = z.infer<typeof TripItem>;

/** Trip item plus flat labels, used by the Phase 2 checklist surface. */
export const TripItemWithLabels = BaseTripItem.extend({
  labels: z.array(z.string()),
}).refine((item) => item.originMasterListId === null || item.originMasterListName !== null, {
  message: 'originMasterListName is required when originMasterListId is set',
  path: ['originMasterListName'],
});
export type TripItemWithLabels = z.infer<typeof TripItemWithLabels>;

/**
 * Manual-add body (api §T16). Server sets `tripId` from the path, `createdBy`
 * = current user, `position` = append. `handledBy` defaults to `createdBy` when
 * omitted (§10 invariant 3). `forPersonId` has NO default (blank = shared).
 * Origin fields stay NULL for manual adds.
 */
export const CreateTripItemInput = z
  .object({
    name: name(),
    description: longText().optional(),
    forPersonId: uuid().nullable().optional(),
    handledBy: uuid().nullable().optional(),
    note: z.string().trim().max(2000).nullable().optional(),
    // Optional: quick-add an item into a list already in this trip. The server
    // resolves the snapshot name from an existing item of that origin; an unknown
    // origin falls back to a manual (null-origin) add.
    originMasterListId: uuid().nullable().optional(),
    // Optional: add into a trip-local list (a named group with no library list
    // behind it). Only meaningful when originMasterListId is absent/null.
    originMasterListName: name().nullable().optional(),
    // Optional correlation id to the source library item (set when reconcile pulls
    // a specific master item in, so the new trip item is linked from the start).
    originMasterListItemId: uuid().nullable().optional(),
  })
  .refine((body) => !(body.originMasterListId && body.originMasterListName), {
    message: 'originMasterListName is only allowed without originMasterListId',
    path: ['originMasterListName'],
  });
export type CreateTripItemInput = z.infer<typeof CreateTripItemInput>;

/**
 * Edit body (api §T16) — all optional. `status` and the question axis are
 * mutated through their own dedicated endpoints (T20) so they stay orthogonal;
 * `position` only via reorder. Origin snapshot is read-only after pull-in.
 */
export const UpdateTripItemInput = z
  .object({
    name: name(),
    description: longText(),
    forPersonId: uuid().nullable(),
    handledBy: uuid().nullable(),
    note: z.string().trim().max(2000).nullable(),
    // Correlation id to a library item (set by reconcile when pushing an item
    // back to the library, so the trip item links to its new source). The server
    // verifies the target belongs to this item's origin list.
    originMasterListItemId: uuid().nullable(),
    /**
     * Compare-and-set for the text fields: the values the edit was based on.
     * When present, the server rejects the write with 409 CONFLICT (details
     * `{ current }`) if the stored value differs — someone else changed it
     * since you started typing. Omit it for plain last-write-wins.
     */
    expect: z
      .object({
        name: z.string(),
        description: z.string().nullable(),
      })
      .partial(),
  })
  .partial();
export type UpdateTripItemInput = z.infer<typeof UpdateTripItemInput>;

/** `details` of a 409 CONFLICT from PATCH /trips/:tripId/items/:itemId. */
export const TripItemConflict = z.object({
  current: z.object({
    name: z.string(),
    description: z.string().nullable(),
  }),
});
export type TripItemConflict = z.infer<typeof TripItemConflict>;

/** Status-only update (T20) — independent of the question axis (PRD §10). */
export const UpdateTripItemStatusInput = z.object({
  status: z.enum(['todo', 'done']),
});
export type UpdateTripItemStatusInput = z.infer<typeof UpdateTripItemStatusInput>;

/** "Final check" toggle — confirming an item is physically packed. */
export const UpdateTripItemFinalInput = z.object({
  finalChecked: z.boolean(),
});
export type UpdateTripItemFinalInput = z.infer<typeof UpdateTripItemFinalInput>;

/** Flag-only update — independent of done/todo status. */
export const UpdateTripItemFlagInput = z.object({
  flagged: z.boolean(),
});
export type UpdateTripItemFlagInput = z.infer<typeof UpdateTripItemFlagInput>;

/**
 * Question-axis update (T20) — set/clear independently of `status`. When
 * `questionOpen` is false the API clears `questionText`; the handler enforces
 * the cross-field meaning (PRD §10).
 */
export const UpdateTripItemQuestionInput = z.discriminatedUnion('questionOpen', [
  z.object({
    questionOpen: z.literal(true),
    questionText: z.string().trim().min(1).max(2000),
  }),
  z.object({
    questionOpen: z.literal(false),
    questionText: z.string().trim().max(2000).nullable().optional(),
  }),
]);
export type UpdateTripItemQuestionInput = z.infer<typeof UpdateTripItemQuestionInput>;

/**
 * Addresses one list *within a trip* — the grouping key over trip items:
 * - `{ originMasterListId }` — a list pulled from the library (server derives the
 *   snapshot name; callers never pass both fields).
 * - `{ originMasterListId: null, originMasterListName }` — a trip-local list (a
 *   named group with no library list behind it).
 * - `{ originMasterListId: null, originMasterListName: null }` — the
 *   "Manually added items" bucket.
 */
export const TripListTarget = z
  .object({
    originMasterListId: uuid().nullable(),
    originMasterListName: name().nullable().default(null),
  })
  .refine((target) => !(target.originMasterListId && target.originMasterListName), {
    message: 'originMasterListName is only allowed without originMasterListId',
    path: ['originMasterListName'],
  });
export type TripListTarget = z.infer<typeof TripListTarget>;

/**
 * Grouping key for a list within a trip. Every surface that buckets trip items
 * (or their events) by list must use this one definition: a library-backed list
 * keys by its origin id, a trip-local list by `local:<name>`, and null-origin
 * null-name items form the `manual` bucket.
 */
export function tripListKey(item: {
  originMasterListId: string | null;
  originMasterListName: string | null;
}): string {
  if (item.originMasterListId) {
    return item.originMasterListId;
  }
  if (item.originMasterListName) {
    return `local:${item.originMasterListName}`;
  }
  return 'manual';
}

/** Remove a whole list from a trip in one operation — addressed as a
 * `TripListTarget`. Distinct from deleting items: it's one action and records a
 * single `list_removed` audit event. */
export const RemoveTripListInput = TripListTarget;
export type RemoveTripListInput = z.infer<typeof RemoveTripListInput>;

/** Upper bound on one move call — above pulled-list sizes, below abuse. */
export const MOVE_TRIP_ITEMS_MAX = 200;

/** Move existing trip items into another list on the same trip (the target may
 * be a pulled list, a trip-local list — creating it if it doesn't exist yet — or
 * the manual bucket). Status, labels, comments, links, and history ride along;
 * the library correlation (`originMasterListItemId`) is cleared. */
export const MoveTripItemsInput = z
  .object({
    itemIds: z.array(uuid()).min(1).max(MOVE_TRIP_ITEMS_MAX),
    target: TripListTarget,
  })
  .refine((body) => new Set(body.itemIds).size === body.itemIds.length, {
    message: 'itemIds must not contain duplicates',
    path: ['itemIds'],
  });
export type MoveTripItemsInput = z.infer<typeof MoveTripItemsInput>;

/** Push a trip-local list up to the Library: creates a master list named after
 * the group, copies its items in, and links the trip items to the new list so
 * the push/pull audit works from then on. */
export const PromoteTripListInput = z.object({
  originMasterListName: name(),
});
export type PromoteTripListInput = z.infer<typeof PromoteTripListInput>;

export const PromoteTripListResult = z.object({
  masterList: MasterList,
  items: z.array(TripItemWithLabels),
});
export type PromoteTripListResult = z.infer<typeof PromoteTripListResult>;

/** Assign several items to one trip member (or clear them) in one call — the
 * "assign this whole list to …" action. `handledBy` must be the trip owner or a
 * collaborator. */
export const AssignTripItemsInput = z
  .object({
    itemIds: z.array(uuid()).min(1).max(200),
    handledBy: uuid().nullable(),
  })
  .refine((body) => new Set(body.itemIds).size === body.itemIds.length, {
    message: 'itemIds must not contain duplicates',
    path: ['itemIds'],
  });
export type AssignTripItemsInput = z.infer<typeof AssignTripItemsInput>;

/** Rename a pulled list's name on this trip only (the library link stays). */
export const RenameTripListInput = z.object({
  originMasterListId: uuid(),
  name: name(),
});
export type RenameTripListInput = z.infer<typeof RenameTripListInput>;

/** Bulk status / flag change — the selection bar's Pack, Unpack, Flag, Unflag. */
export const BulkUpdateTripItemsInput = z
  .object({
    itemIds: z.array(uuid()).min(1).max(200),
    patch: z
      .object({
        status: z.enum(['todo', 'done']).optional(),
        flagged: z.boolean().optional(),
      })
      .refine((patch) => patch.status !== undefined || patch.flagged !== undefined, {
        message: 'patch must change something',
      }),
  })
  .refine((body) => new Set(body.itemIds).size === body.itemIds.length, {
    message: 'itemIds must not contain duplicates',
    path: ['itemIds'],
  });
export type BulkUpdateTripItemsInput = z.infer<typeof BulkUpdateTripItemsInput>;

/** Bulk remove (soft delete, restorable) — the selection bar's Remove. */
export const BulkRemoveTripItemsInput = z
  .object({ itemIds: z.array(uuid()).min(1).max(200) })
  .refine((body) => new Set(body.itemIds).size === body.itemIds.length, {
    message: 'itemIds must not contain duplicates',
    path: ['itemIds'],
  });
export type BulkRemoveTripItemsInput = z.infer<typeof BulkRemoveTripItemsInput>;

/** Batch reorder body for Trip Items (T27). */
export const ReorderTripItemsInput = z
  .object({
    itemIds: z.array(uuid()).min(1),
  })
  .refine((body) => new Set(body.itemIds).size === body.itemIds.length, {
    message: 'itemIds must not contain duplicates',
    path: ['itemIds'],
  });
export type ReorderTripItemsInput = z.infer<typeof ReorderTripItemsInput>;

/** Kinds of change shown on a trip item's activity feed. */
export const TripItemEventType = z.enum([
  'created',
  'status_changed',
  'renamed',
  'note_changed',
  'for_person_changed',
  'assigned',
  'unassigned',
  'label_added',
  'label_removed',
  'question_opened',
  'question_resolved',
  'bought',
  'comment_added',
  'comment_edited',
  'comment_deleted',
  'flagged',
  'unflagged',
  'lesson_created',
  'item_linked',
  'item_unlinked',
  'deleted',
  'moved',
  'list_removed',
  'list_promoted',
  'restored',
  'list_renamed',
  // List-level: items were reordered (data: { itemIds }).
  'reordered',
  // The "final check" (physically packed) mark was set or cleared.
  'final_changed',
]);
export type TripItemEventType = z.infer<typeof TripItemEventType>;

export const ListTripItemEventsQuery = z.object({
  origin: z.string().optional(),
  /** Only events newer than this event id — the change-detection cursor. */
  after: uuid().optional(),
});
export type ListTripItemEventsQuery = z.infer<typeof ListTripItemEventsQuery>;

/** One entry in a trip item's activity feed (read-only). */
export const TripItemEvent = z.object({
  id: uuid(),
  tripId: uuid(),
  // Null for list-level events (e.g. `list_removed`) that aren't tied to one item.
  tripItemId: uuid().nullable(),
  tripItemName: z.string().nullable(),
  originMasterListId: uuid().nullable(),
  originMasterListName: z.string().nullable(),
  itemDeletedAt: z.string().datetime().nullable(),
  type: TripItemEventType,
  data: z.record(z.string(), z.unknown()).nullable(),
  actorId: uuid().nullable(),
  actorName: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type TripItemEvent = z.infer<typeof TripItemEvent>;

/** One collaborative comment on a trip item. */
export const TripItemComment = z.object({
  id: uuid(),
  tripId: uuid(),
  tripItemId: uuid(),
  body: z.string(),
  createdBy: uuid(),
  createdByUser: UserSummary.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  edited: z.boolean(),
});
export type TripItemComment = z.infer<typeof TripItemComment>;

export const CreateTripItemCommentInput = z.object({
  body: z.string().trim().min(1).max(2000),
});
export type CreateTripItemCommentInput = z.infer<typeof CreateTripItemCommentInput>;

export const UpdateTripItemCommentInput = CreateTripItemCommentInput;
export type UpdateTripItemCommentInput = z.infer<typeof UpdateTripItemCommentInput>;
